import { apiPost, apiPostNoContent } from './client';
import { toDocumentFileReference } from './documents';

const DOCUMENTS_SECTION = 'documents';

export interface OrderItemDTO {
  id: string;
  productId: string;
  quantity: number;
  costPrice: string;
  salePrice: string;
  diff: string;
}

export interface OrderDocumentFile {
  id: string;
  uri: string;
  filename?: string;
  size?: number;
}

export interface OrderDocumentItem {
  id: string;
  createdAt: string;
  file: OrderDocumentFile;
}

export interface OrderDTO {
  id: string;
  title: string;
  customer: string;
  quantity: number;
  date: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  items?: OrderItemDTO[];
  documents?: OrderDocumentItem[];
}

/** Файл для documents (UploadedFile-совместимый). */
export interface OrderDocumentFileInput {
  id: string;
  uri: string;
  filename?: string;
  size?: number;
}

export interface OrderListItemDTO {
  id: string;
  title: string;
  customer: string;
  quantity: number;
  date: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface FindOrdersParams {
  title?: string;
  customer?: string;
  page?: number;
  itemsPerPage?: number;
  order?: Array<{ field: string; direction: string }>;
}

export interface FindOrdersResponse {
  items: OrderListItemDTO[];
  count: number;
}

export interface FindByIDsOrdersResponse {
  items: Record<string, OrderDTO>;
}

export interface CreateOrderItemParams {
  productId: string;
  quantity: number;
  costPrice: string;
  salePrice: string;
}

export interface CreateOrderParams {
  title: string;
  customer: string;
  date: string;
  status: string;
  items: CreateOrderItemParams[];
  documents?: Array<{ file: OrderDocumentFileInput }>;
}

export interface UpdateOrderItemParams {
  productId: string;
  quantity: number;
  costPrice: string;
  salePrice: string;
}

export interface UpdateOrderParams {
  id: string;
  title: string;
  customer: string;
  date: string;
  status: string;
  items: UpdateOrderItemParams[];
  documents: Array<{ file: OrderDocumentFileInput }>;
}

export const ORDER_STATUSES = ['new', 'in_progress', 'done', 'cancelled'] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  new: 'Новый',
  in_progress: 'В работе',
  done: 'Выполнен',
  cancelled: 'Отменён',
};

export async function findOrders(
  params: FindOrdersParams = {},
): Promise<FindOrdersResponse> {
  return apiPost<FindOrdersResponse>('/api/orders/find', {
    title: params.title ?? '',
    customer: params.customer ?? '',
    page: params.page ?? 1,
    itemsPerPage: params.itemsPerPage ?? 20,
    order: params.order ?? [{ field: 'createdAt', direction: 'desc' }],
  });
}

export async function findOrdersByIDs(ids: string[]): Promise<FindByIDsOrdersResponse> {
  return apiPost<FindByIDsOrdersResponse>('/api/orders/find-by-ids', { ids });
}

export async function createOrder(params: CreateOrderParams): Promise<OrderDTO> {
  const documents =
    params.documents?.map((d) => ({
      file: toDocumentFileReference(d.file, DOCUMENTS_SECTION),
    })) ?? [];

  return apiPost<OrderDTO>('/api/orders/create', {
    title: params.title,
    customer: params.customer,
    date: params.date,
    status: params.status,
    items: params.items,
    documents,
  });
}

export async function updateOrder(params: UpdateOrderParams): Promise<OrderDTO> {
  const documents = params.documents.map((d) => ({
    file: toDocumentFileReference(d.file, DOCUMENTS_SECTION),
  }));

  return apiPost<OrderDTO>('/api/orders/update', {
    id: params.id,
    title: params.title,
    customer: params.customer,
    date: params.date,
    status: params.status,
    items: params.items,
    documents,
  });
}

export async function deleteOrders(ids: string[]): Promise<void> {
  await apiPostNoContent('/api/orders/delete', { ids });
}
